import React, { useState, MouseEvent } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Backdrop,
} from '@mui/material';
import { AdminCampaignTypes, Status } from '../types';
import { Icons } from '@/icons';
import StatusBadge from '@/components/StatusBadge';
import { MENU_MAP, MenuDescriptor } from './MenuMap';
import RemoteFile from '@/helper/RemoteFile';
import { formatLocalDate } from '@/utils/FormateLocalDate';
import { useDispatch } from 'react-redux';
import { deleteCampaign, updateCampaignStatus } from '@/store/Campaign';
import SuccessDialog from '@/components/dialog/SuccessDialog';
import IconGallery from '@/utils/IconGallery';

interface Props {
  campaign: AdminCampaignTypes;
}

const CampaignRow: React.FC<Props> = ({ campaign }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isApproveSuccessOpen, setIsApproveSuccessOpen] = useState(false);
  const [isRejectSuccessOpen, setIsRejectSuccessOpen] = useState(false);
  const [isRemoveSuccessOpen, setIsRemoveSuccessOpen] = useState(false);

  const dispatch = useDispatch<any>();

  // Open menu when icon button is clicked
  const openMenu = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setMenuOpen(true);
  };

  // Close menu
  const closeMenu = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

const formatStatus = (raw: string): Status => {
  const formatted = raw
    .replace(/_/g, ' ')
    .split(' ')
    .map(
      w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join(' ') as Status;
  return (formatted);
};

  const status = formatStatus(campaign.status)

  const descriptors: MenuDescriptor[] =
    MENU_MAP[status] ?? MENU_MAP['Pending'];

  const handleApprove = async () => {
    await dispatch(
      updateCampaignStatus({
        campaignId: campaign.id,
        status: 'approved',
      }),
    );
    setIsApproveSuccessOpen(true);
  };

  const handleReject = async () => {
    await dispatch(
      updateCampaignStatus({
        campaignId: campaign.id,
        status: 'rejected',
      }),
    );
    setIsRejectSuccessOpen(true);
  };

  const handleHold = () => {
    // Dispatch action to put campaign on hold
  };

  const handleRemove = async () => {
    await dispatch(deleteCampaign(campaign.id));
    setIsRemoveSuccessOpen(true);
  };

  return (
    <>
      <tr className='border-b'>
        <td className='p-2'>
          <RemoteFile
            keyPath={campaign.image_url}
            key={campaign.gamic_title}
            className='h-[84px] w-[56px] object-cover rounded'
          />
        </td>
        <td className='p-4'>{formatLocalDate(campaign.created_at)}</td>
        <td className='p-4'>{campaign.gamic_title}</td>
        <td className='p-4'>{`${formatLocalDate(campaign.start_date)} - ${formatLocalDate(campaign.end_date)}`}</td>
        <td className='p-4'>$ {campaign.total_budget}</td>
        <td className='p-4'>$ {campaign.spent}</td>
        <td className='p-4'>{campaign.impressions}</td>
        <td className='p-4'>
          <StatusBadge status={campaign.status} />
        </td>
        <td className='p-4 relative'>
          <IconButton size='small' onClick={openMenu}>
            <Icons.Menu />
          </IconButton>
          {menuOpen && (
            <Backdrop
              open
              onClick={closeMenu}
              sx={{
                position: 'fixed',
                zIndex: (theme) => theme.zIndex.modal - 1,
                bgcolor: 'rgba(0,0,0,0.3)',
              }}
            />
          )}
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: 291,
                borderRadius: 2,
                overflow: 'visible',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 10,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  boxShadow: '0px -5px 2px rgba(0,0,0,.1)',
                },
              },
            }}
          >
            {descriptors.map(({ key, icon, label, to, action }) => (
              <MenuItem
                sx={{ width: '100%', textAlign: 'left' }}
                key={key}
                {...(to
                  ? { component: NavLink, to, state: { campaign } }
                  : { component: 'button' })}
                onClick={() => {
                  closeMenu();
                  if (key === 'approve') handleApprove();
                  if (key === 'reject') handleReject();
                  if (key === 'hold') handleHold();
                  if (key === 'remove') handleRemove();
                  action?.();
                }}
              >
                <ListItemIcon sx={{ height: '32px', width: '32px' }}>
                  {icon}
                </ListItemIcon>
                <ListItemText primary={label} />
              </MenuItem>
            ))}
          </Menu>
                  {/* Success Dialog */}
                  <SuccessDialog
                    open={isApproveSuccessOpen}
                    onClose={() => setIsApproveSuccessOpen(false)}
                    icon={<Icons.Approved className="h-[160px] w-[160px]" />}
                    heading="Campaign Approved!"
                    subheading="Campaign is approved by admins."
                  />
                  <SuccessDialog
                    open={isRejectSuccessOpen}
                    onClose={() => setIsRejectSuccessOpen(false)}
                    icon={<Icons.Rejected className="h-[160px] w-[160px]" />}
                    heading="Campaign Rejected!"
                    subheading="Campaign is not approved by admins."
                  />
                  <SuccessDialog
                    open={isRemoveSuccessOpen}
                    onClose={() => setIsRemoveSuccessOpen(false)}
                    icon={<Icons.Trash className="h-[160px] w-[160px]" />}
                    heading="Campaign Removed!"
                    subheading="Gamic is removed from the history."
                  />
        </td>
      </tr>
    </>
  );
};

export default CampaignRow;
